/**
 * Reuses the existing Dutch Journal photography on the English travel guides.
 *
 * The English articles are 1:1 counterparts of the Dutch ones, so we point at
 * the same WebP assets in /public/journal (no new images are generated) but
 * ship native English alt text, image titles and captions.
 */

export type EnJournalImage = {
  src: string;
  srcSet: string;
  alt: string;
  title: string;
  caption: string;
  width: number;
  height: number;
};

import { EN_BLOG_SOURCE } from "./journal-en-blog.data";

const WIDTH = 1200;
const HEIGHT = 630;

type Entry = { asset: string; alt: string; title: string; caption: string };

/** English journal slug -> shared asset base name + English image copy. */
const IMAGES: Record<string, Entry> = {
  "taxi-or-train": {
    asset: "taxi-of-trein",
    alt: "Dutch intercity train at a platform with a taxi waiting on the station square",
    title: "Taxi or train in the Netherlands",
    caption: "The train is fast between cities; a taxi takes you door to door.",
  },
  "taxi-or-public-transport": {
    asset: "taxi-of-openbaar-vervoer",
    alt: "Tram and city bus at a stop in a Dutch city with a taxi waiting nearby",
    title: "Taxi or public transport",
    caption: "With luggage or late at night, a taxi beats changing trains and buses.",
  },
  "taxi-or-your-own-car": {
    asset: "taxi-of-eigen-auto",
    alt: "Private car on a Dutch driveway with a taxi waiting at the kerb",
    title: "Taxi or your own car",
    caption: "Fuel, parking and tolls often make driving yourself the pricier option.",
  },
  "taxi-to-the-airport-or-drive-yourself": {
    asset: "taxi-naar-het-vliegveld-of-zelf-rijden",
    alt: "Taxi on a Dutch motorway heading towards the airport",
    title: "Taxi to the airport or drive yourself",
    caption: "The real difference is parking and time, not just the fare.",
  },
  "airport-parking-or-taxi": {
    asset: "parkeren-bij-het-vliegveld-of-taxi",
    alt: "Rows of parked cars at a Dutch long-stay airport car park",
    title: "Airport parking or a taxi",
    caption: "Long-stay parking adds up per day; a taxi is a single fixed price.",
  },
  "when-is-a-taxi-the-smartest-choice": {
    asset: "wanneer-is-een-taxi-de-slimste-keuze",
    alt: "Rainy Dutch street with cyclists and a taxi waiting at the kerb",
    title: "When is a taxi the smartest choice",
    caption: "Rain, luggage and early flights are when a taxi is the calmest option.",
  },
  "fixed-price-versus-taximeter": {
    asset: "vaste-prijs-versus-taxameter",
    alt: "Taximeter on the dashboard of a taxi",
    title: "Fixed price versus taximeter",
    caption: "A meter keeps running in traffic; a fixed price does not move.",
  },
  "what-time-to-leave-for-the-airport": {
    asset: "luchthaventaxi-hoe-laat-vertrekken",
    alt: "Clock in an airport terminal above travellers walking to departures",
    title: "What time to leave for the airport",
    caption: "Count back from your flight: check-in, security and travel time.",
  },
  "taxi-to-the-airport-for-an-early-flight": {
    asset: "taxi-naar-het-vliegveld-bij-een-vroege-vlucht",
    alt: "Taxi with headlights on in a Dutch street before sunrise",
    title: "Taxi to the airport for an early flight",
    caption: "For night-time pick-ups the driver is at your door on time.",
  },
  "taxi-to-the-airport-with-children": {
    asset: "taxi-naar-het-vliegveld-met-kinderen",
    alt: "Family with children and suitcases boarding a minibus taxi in a Dutch street",
    title: "Taxi to the airport with children",
    caption: "Board at your front door and step out at the terminal.",
  },
  "to-the-airport-with-a-lot-of-luggage": {
    asset: "met-veel-bagage-naar-het-vliegveld",
    alt: "Driver loading large suitcases into a white taxi outside a Dutch house",
    title: "To the airport with a lot of luggage",
    caption: "Lots of cases? Then an estate car or minibus taxi is sent.",
  },
  "delayed-train-or-flight": {
    asset: "vertraging-van-trein-of-vlucht",
    alt: "Traveller with a suitcase checking the departures board in an arrivals hall",
    title: "A delayed train or flight",
    caption: "We track your flight number, so the driver shifts with your landing.",
  },
  "taxi-to-a-train-station": {
    asset: "taxi-naar-een-station",
    alt: "Taxi in front of a Dutch railway station with bicycles parked outside",
    title: "Taxi to a train station",
    caption: "Door-to-door to the station, even with plenty of luggage.",
  },
  "taxi-to-a-hospital": {
    asset: "taxi-naar-het-ziekenhuis",
    alt: "White taxi at the entrance of a Dutch hospital",
    title: "Taxi to a hospital appointment",
    caption: "Pick-up at the main entrance, with the price agreed in advance.",
  },
  "taxi-to-a-hotel": {
    asset: "taxi-naar-een-hotel",
    alt: "White taxi outside a hotel on a Dutch canal",
    title: "Taxi to a hotel",
    caption: "From the hotel door to the airport for a price fixed beforehand.",
  },
  "taxi-to-a-stadium-or-event": {
    asset: "taxi-naar-een-stadion-of-evenement",
    alt: "Taxi outside a football stadium on a match day in the Netherlands",
    title: "Taxi to a stadium or event",
    caption: "At stadiums and festivals we agree a pick-up point away from the crowds.",
  },
  "taxi-to-a-city-centre-or-shopping-area": {
    asset: "taxi-naar-een-stadscentrum-of-winkelgebied",
    alt: "Taxi in a Dutch city centre with a cobbled shopping street",
    title: "Taxi to a city centre or shopping area",
    caption: "In car-free centres we pick a practical pick-up point together.",
  },
  "travelling-with-children-in-a-taxi": {
    asset: "met-kinderen-in-de-taxi",
    alt: "Child secured in a booster seat on the back seat of a taxi",
    title: "Travelling with children in a taxi",
    caption: "You can always bring your own child seat or booster seat.",
  },
  "travelling-with-pets-in-a-taxi": {
    asset: "met-huisdieren-in-de-taxi",
    alt: "Dog in a travel crate in the boot of a white estate car",
    title: "Travelling with pets in a taxi",
    caption: "Pets are welcome; mention them when booking so the car fits.",
  },
  "travelling-with-reduced-mobility": {
    asset: "reizen-als-u-slecht-ter-been-bent",
    alt: "Driver helping an older woman with a walking stick into a white taxi",
    title: "Travelling with reduced mobility",
    caption: "The driver helps you in and out and handles your luggage.",
  },
  "travelling-as-a-group": {
    asset: "reizen-met-een-groep",
    alt: "Group of travellers with suitcases beside a white minibus taxi",
    title: "Travelling as a group",
    caption: "One minibus for the whole group usually beats separate cars.",
  },
  "minibus-taxi-for-several-passengers": {
    asset: "taxibus-met-meerdere-personen",
    alt: "White minibus taxi with its sliding door open and passengers boarding",
    title: "Minibus taxi for several passengers",
    caption: "From five passengers a minibus with luggage space is sent.",
  },
  "how-far-in-advance-to-book-a-taxi": {
    asset: "hoe-lang-van-tevoren-taxi-reserveren",
    alt: "Smartphone showing a calendar while a taxi waits in a Dutch street",
    title: "How far in advance to book a taxi",
    caption: "Booking early gives certainty, also in busy holiday periods.",
  },
  "taxi-at-night": {
    asset: "taxi-in-de-nacht",
    alt: "White taxi on an empty Dutch street at night with wet tarmac",
    title: "Taxi at night",
    caption: "At night we drive for exactly the same fixed price as during the day.",
  },
  "taxi-during-rush-hour": {
    asset: "taxi-tijdens-de-spits",
    alt: "Busy Dutch motorway in a traffic jam with yellow number plates",
    title: "Taxi during rush hour",
    caption: "With a fixed price you do not pay extra for the traffic jam.",
  },
  "change-or-cancel-a-booking": {
    asset: "rit-wijzigen-of-annuleren",
    alt: "Someone changing a booking on a laptop at a kitchen table",
    title: "Change or cancel a booking",
    caption: "Up to 24 hours before pick-up you change your ride online yourself.",
  },
  "business-taxi-travel-and-invoicing": {
    asset: "zakelijk-taxivervoer-facturatie",
    alt: "Business traveller stepping out of a white taxi at a Dutch office",
    title: "Business taxi travel and invoicing",
    caption: "Business rides come with a VAT invoice and your company details.",
  },
  "benefits-of-booking-in-advance": {
    asset: "voordelen-van-vooraf-reserveren",
    alt: "White taxi waiting outside a Dutch canal house in the morning",
    title: "Benefits of booking in advance",
    caption: "Booking ahead means: price known, driver scheduled, no waiting.",
  },
};

export function enJournalImage(slug: string): EnJournalImage | null {
  const blog = IMAGES[slug] ? null : EN_BLOG_SOURCE.find((b) => b.slug === slug);
  const entry: Entry | undefined =
    IMAGES[slug] ?? (blog ? { asset: blog.nlSlug, alt: blog.imageAlt, title: blog.title, caption: blog.imageCaption } : undefined);
  if (!entry) return null;
  return {
    src: `/journal/${entry.asset}-1200.webp`,
    srcSet: [480, 800, 1200].map((w) => `/journal/${entry.asset}-${w}.webp ${w}w`).join(", "),
    alt: entry.alt,
    title: entry.title,
    caption: entry.caption,
    width: WIDTH,
    height: HEIGHT,
  };
}

/** Absolute URL for social preview tags (og:image / twitter:image). */
export function enJournalImageUrl(slug: string, site = "https://taxiritje.nl") {
  const image = enJournalImage(slug);
  return image ? `${site}${image.src}` : null;
}
